import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ChartBarGradient,
  ChartEmptyState,
  ChartPanel,
} from '../../components/stats/ChartPrimitives';
import { chartGradientFill, useChartGradientId } from '../../components/stats/chartGradient';
import {
  CHART_AXIS_PROPS,
  CHART_AXIS_TICK,
  CHART_BAR_PROPS,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from '../../components/stats/chartTheme';

export interface CostChartSeries {
  key: string;
  label: string;
  color: string;
}

interface CostOverTimeChartProps {
  buckets: object[];
  series: CostChartSeries[];
  total: string;
  loading: boolean;
  hasCost: boolean;
  loadingMessage: string;
  emptyMessage: string;
  formatValue: (value: number) => string;
}

const COST_ICON = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6v12m4-9.5C16 7.12 14.21 6 12 6S8 7.12 8 8.5 9.79 11 12 11s4 1.12 4 2.5S14.21 16 12 16s-4-1.12-4-2.5"
    />
  </svg>
);

export function CostOverTimeChart({
  buckets,
  series,
  total,
  loading,
  hasCost,
  loadingMessage,
  emptyMessage,
  formatValue,
}: CostOverTimeChartProps) {
  const gradientBaseId = useChartGradientId('cost-over-time');

  return (
    <ChartPanel
      title="Cost over time"
      icon={COST_ICON}
      iconClassName="bg-warning-50 text-warning-700"
      className="!h-auto"
      summary={
        <div className="text-right">
          <span className="text-lg font-semibold text-slate-700">{total}</span>
          <span className="ml-1 text-[10px] uppercase text-slate-500">total</span>
        </div>
      }
    >
      <div className="mb-2 flex flex-wrap justify-end gap-x-4 gap-y-1">
        {series.map((item) => (
          <span key={item.key} className="inline-flex items-center gap-1.5 text-[10px] text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}
          </span>
        ))}
      </div>

      <div className="flex h-64">
        {loading && !hasCost ? (
          <ChartEmptyState>{loadingMessage}</ChartEmptyState>
        ) : hasCost ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={buckets}
              margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
              barCategoryGap="18%"
            >
              <defs>
                {series.map((item) => (
                  <ChartBarGradient
                    key={item.key}
                    id={`${gradientBaseId}-${item.key}`}
                    color={item.color}
                  />
                ))}
              </defs>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis
                {...CHART_AXIS_PROPS}
                dataKey="label"
                tick={CHART_AXIS_TICK}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                {...CHART_AXIS_PROPS}
                tick={CHART_AXIS_TICK}
                tickFormatter={(value: number) => formatValue(value)}
              />
              <Tooltip
                formatter={(value: number, name: string) => [formatValue(value), name]}
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
              />
              {series.map((item, index) => (
                <Bar
                  {...CHART_BAR_PROPS}
                  key={item.key}
                  dataKey={item.key}
                  name={item.label}
                  stackId="a"
                  fill={chartGradientFill(`${gradientBaseId}-${item.key}`)}
                  radius={index === series.length - 1 ? [2, 2, 0, 0] : undefined}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ChartEmptyState>{emptyMessage}</ChartEmptyState>
        )}
      </div>
    </ChartPanel>
  );
}
